import { useReducer, useRef, useContext, memo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useParams } from "react-router-dom";
import { UserDataContext } from "../app/App";
import {
    ImageList,
    ImageListItem,
    ImageListItemBar,
    Typography,
    Box,
    CircularProgress,
    Modal,
} from "@mui/material";
import { Close as CloseIcon } from '@mui/icons-material';
import { useGetLocationImages, useGetProjectLine } from "../api";
import Layout from "../components/Layout/Layout";
import UploadPhotosButton from "../components/buttons/UploadImagesButton/index";
import { isPrivileged } from "../utils/privilegeUtils";
import { IconButton } from "../../node_modules/@material-ui/core/index";


const ImageItem = memo(({ item, onOpen }) => (
    <ImageListItem key={item.img} onClick={() => onOpen(item)}>
        <img
            src={`${item.img}&w=164&h=164&fit=crop&auto=format`}
            srcSet={`${item.img}&w=164&h=164&fit=crop&auto=format&dpr=2 2x`}
            alt={item.name}
            loading="lazy"
        />
        <ImageListItemBar
            title={item.name.replace(/\.[^/.]+$/, "")}
            subtitle={<span>{item.metadata.tegund}</span>}
            position="below"
        />
    </ImageListItem>
));


export default function PhotoAlbumPage() {
    const uploadButtonRef = useRef();
    const { user } = useContext(UserDataContext);
    const { id: projectLineId } = useParams();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const [state, setState] = useReducer(
        (current, update) => ({ ...current, ...update }),
        { projectLine: null, images: [], location: null, modalOpen: false, selectedImage: null }
    );

    /* API QUERIES */

    // Get project line
    const { isLoading: isLoadingProjectLine } = useGetProjectLine(projectLineId, {
        enabled: !!projectLineId,
        refetchOnWindowFocus: false,
        onSuccess: (projectLine) => {
            // Set the project line and the location as its address
            setState({ location: projectLine?.project?.address ?? {}, projectLine: projectLine ?? {} });
        },
        onError: () => {
            enqueueSnackbar("Error getting location", {
                variant: "error",
                anchorOrigin: { vertical: "bottom", horizontal: "center" },
                preventDuplicate: true,
            });
        },
    });
    // Get project line location images
    const { data, isLoading: isLoadingImages } = useGetLocationImages(state.location?.id, {
        enabled: !!state.location?.id,
        onError: () => {
            enqueueSnackbar("Failed getting images", {
                variant: "error",
                anchorOrigin: { vertical: "bottom", horizontal: "center" },
            });
        },
    });

    useEffect(() => {
        if (!data) return;
        const images = Array.isArray(data)
            ? data
                .filter((image) =>
                    image?.metadata?.tegund === state.projectLine?.project?.serviceType?.name
                )
                .map((image) => ({
                    ...image,
                    img: image.serverRelativeUrl,
                })) : [];
        setState({ images: images });
    }, [data])

    const isLoading = isLoadingProjectLine || isLoadingImages;

    /* HELPERS */

    // Set selected image and open modal
    const handleImageOpen = (img) => {
        setState({ selectedImage: img, modalOpen: true });
    };
    // Close modal
    const handleCloseModal = () => {
        setState({ modalOpen: false });
    };

    if (isLoading) {
        return (
            <Layout color="rgb(77 106 201)" title={`${state.location?.name ?? ""}`} subtitle={t("Photos")}>
                <Box display="flex" justifyContent="center">
                    <CircularProgress />
                </Box>
            </Layout>
        );
    }

    const isAdmin = isPrivileged(user.crmPrivileges);

    return (
        <Layout
            color="rgb(77 106 201)"
            title={`${state.location?.name ?? ""}`}
            subtitle={t("Photos")}
            extra={<UploadPhotosButton isAdmin={isAdmin} ref={uploadButtonRef} projectLine={state.projectLine} />}
        >
            <Modal
                open={state.modalOpen}
                onClose={handleCloseModal}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <Box sx={{
                    width: 'auto',
                    overflow: 'auto',
                    position: 'relative',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography>{state.selectedImage?.name.replace(/\.[^/.]+$/, "")}</Typography>
                        <IconButton onClick={handleCloseModal} sx={{ position: 'absolute', right: 8, top: 8 }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    {state.selectedImage && (
                        <img
                            src={state.selectedImage.img}
                            alt={state.selectedImage.name}
                            style={{ maxWidth: '90%', maxHeight: '90%', display: 'block' }} 
                        />
                    )}
                </Box>
            </Modal>
            {state.images.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
                    <Typography variant="h6">No Photos</Typography>
                </Box>
            ) : (
                <ImageList sx={{ width: '100%', height: '70vh', overflow: 'auto' }} cols={2} gap={4}>
                    {state.images.map((item) => (
                        <ImageItem key={item.img} item={item} onOpen={handleImageOpen} />
                    ))}
                </ImageList>
            )}
        </Layout>
    );
}
